import { Button, Card, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { gbp, dateTime } from '../data/format';

const { Text } = Typography;

/**
 * SHAR-811/812 — the Invoices block for a charter request and a charter booking.
 *
 * Deliberately the same shape as the ShareJet booking's block (BookingDetailPage):
 * kind, number, amount, date, in a small borderless table with the same wording,
 * because the ticket asks for "the implementation already used on the ShareJet
 * booking details screen" rather than a second design.
 *
 * Two differences follow from the data rather than from choice. Charter invoices
 * are not Invoice rows - they are rendered on demand from the request and the
 * booking - so there is no id to key on beyond the one the server synthesises,
 * and no stored issue date for the pre-auth beyond the request's own. And there
 * IS a download here, which the ShareJet block does not have: the server can
 * hand out a signed link (SHAR-795) that carries its own credential, so it works
 * from a plain href.
 */
export type CharterInvoiceRow = {
  id: string;
  kind: 'preauth' | 'final';
  number: string;
  amountGbp: number;
  createdAt: string | null;
  downloadUrl: string | null;
  /** SHAR-1106 — what became of a pre-auth hold: held / captured / expired / released.
   *  SHAR-1140 — a final invoice: payment_due (wire owed) or paid. */
  state?: 'held' | 'captured' | 'expired' | 'released' | 'payment_due' | 'paid' | 'cancelled' | null;
  stateAt?: string | null;
  stateReason?: string | null;
};

/**
 * SHAR-1106 (PO, 29 Sep) — the ACTUAL state of the hold, in the tags the
 * ShareJet booking's invoices use (SHAR-1097/1098): Released when it was let go,
 * Captured when it became part of the payment, and - charters only - Expired
 * when it lapsed at the gateway.
 */
export function PreAuthStateTag({ state, at, reason }: { state?: CharterInvoiceRow['state']; at?: string | null; reason?: string | null }) {
  if (!state) return null;
  const t = ({
    // SHAR-1373 follow-up (Pavel, 30 Oct) - a standing hold reads "Pre-authorised" (it read "Paid"
    // since SHAR-1115), as it does on the PDF.
    held: { c: 'blue', l: 'Pre-authorised', verb: 'Pre-authorised' },
    released: { c: 'default', l: 'Released', verb: 'Released' },
    expired: { c: 'red', l: 'Expired', verb: 'Expired' },
    captured: { c: 'green', l: 'Captured', verb: 'Captured' },
  } as Record<string, { c: string; l: string; verb: string }>)[state];
  if (!t) return null; // SHAR-1140 — a final invoice's state is not a hold's
  return (
    <Tooltip title={state === 'held' ? 'Pre-authorisation hold active' : `${t.verb}${at ? ` ${dateTime(at)}` : ''}${reason && state !== 'captured' ? ` — ${reason}` : ''}`}>
      <Tag color={t.c}>{t.l}</Tag>
    </Tooltip>
  );
}

export function CharterInvoicesCard({ invoices }: { invoices?: CharterInvoiceRow[] | null }) {
  return (
    <Card title="Invoices">
      <Table<CharterInvoiceRow>
        rowKey="id"
        dataSource={invoices ?? []}
        pagination={false}
        size="small"
        locale={{ emptyText: 'No invoices' }}
        columns={[
          {
            title: 'Kind',
            dataIndex: 'kind',
            key: 'kind',
            width: 170,
            render: (k: CharterInvoiceRow['kind'], r) =>
              k === 'preauth' ? (
                <Space size={6}>
                  Pre-auth
                  <PreAuthStateTag state={r.state} at={r.stateAt} reason={r.stateReason} />
                </Space>
              ) : (
                <Space size={6}>
                  Final
                  {/* SHAR-1140 — the bill (wire owed) or the paid document. */}
                  {r.state === 'payment_due' ? (
                    <Tooltip title="Payable by bank transfer; not yet received">
                      <Tag color="orange">Payment due</Tag>
                    </Tooltip>
                  ) : r.state === 'paid' ? (
                    <Tag color="green">Paid</Tag>
                  ) : r.state === 'cancelled' ? (
                    // PO, 1 Oct — a wire booking cancelled before its transfer came.
                    <Tooltip title="Booking cancelled before the bank transfer was received; nothing payable">
                      <Tag>Cancelled</Tag>
                    </Tooltip>
                  ) : null}
                </Space>
              ),
          },
          { title: 'Number', dataIndex: 'number', key: 'number', width: 220 },
          {
            title: 'Amount',
            dataIndex: 'amountGbp',
            key: 'amountGbp',
            width: 140,
            render: (v: number) => (v ? `${gbp(v)}` : '—'),
          },
          {
            title: 'Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 200,
            render: (v: string | null) => (v ? dateTime(v) : '—'),
          },
          {
            title: '',
            key: 'download',
            width: 140,
            render: (_, r) =>
              r.downloadUrl ? (
                <Button
                  type="link"
                  size="small"
                  icon={<DownloadOutlined />}
                  href={r.downloadUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Download
                </Button>
              ) : (
                <Text type="secondary">—</Text>
              ),
          },
        ]}
      />
    </Card>
  );
}
